import React, {
  Props,
  PropsWithChildren,
  PropsWithoutRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import algoliasearch from "algoliasearch/lite"
import {
  Configure,
  Highlight,
  Hits,
  Index,
  InstantSearch,
  useHits,
  useSearchBox,
} from "react-instantsearch-hooks-web"
import { CgClose } from "react-icons/cg"
import { Link } from "gatsby"
import { Pill } from "./layout/Pill"
import { useOutsideAlerter } from "./layout/useOutsideAlerter"

type SearchProps = {
  closeCb: () => void
}

export const Search = ({ closeCb }: SearchProps) => {
  const ref = useRef()
  useOutsideAlerter(ref, closeCb)
  useEffect(() => {
    const isEsc = e => e.key === "Escape" && closeCb()
    document.addEventListener("keydown", isEsc, false)

    return () => {
      document.removeEventListener("keydown", isEsc, false)
    }
  }, [])

  const searchClient = useMemo(
    () =>
      algoliasearch(
        process.env.GATSBY_ALGOLIA_APP_ID as string,
        process.env.GATSBY_ALGOLIA_SEARCH_KEY as string,
      ),
    [],
  )

  const [query, setQuery] = useState("")

  return (
    <div>
      <CgClose
        size="3rem"
        className="cursor-pointer fixed right-4 top-4 z-50 p-2 bg-white bg-opacity-70 border border-gray-700 rounded-full"
        onClick={closeCb}
      />
      <div className="fixed backdrop-blur-md w-screen h-screen	z-40 bg-white bg-opacity-30 top-0 left-0 overflow-scroll">
        <div
          className="relativ max-w-2xl mx-auto  p-6 pt-20 sm:p-12 bg-white rounded-2xl sm:my-8 min-h-full sm:min-h-0 shadow-xl "
          ref={ref}
        >
          <InstantSearch searchClient={searchClient}>
            <SearchBox query={query} setQueryCb={setQuery} />

            {query.length > 2 && (
              <div className="flex flex-col space-y-4 py-4">
                <Index indexName="Sidor">
                  <Configure hitsPerPage={3} />
                  <SidHits />
                </Index>
                <Index indexName="Politik">
                  <Configure hitsPerPage={5} />
                  <PolitikHits />
                </Index>
                <Index indexName="Blog">
                  <Configure hitsPerPage={5} />
                  <PostHits />
                </Index>
              </div>
            )}
          </InstantSearch>
        </div>
      </div>
    </div>
  )
}

export const SearchBox = ({ query, setQueryCb }) => {
  const { refine } = useSearchBox()

  useEffect(() => {
    refine(query)
    query.length > 2 && refine(query)
    return () => {}
  }, [query])

  return (
    <form>
      <input
        autoFocus
        className="rounded-full w-full px-4 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-500"
        placeholder="Sök"
        type="text"
        aria-label="Search"
        onChange={e => {
          const query = e.target.value
          setQueryCb(query)
        }}
        value={query}
      ></input>
    </form>
  )
}

const PostHits = () => {
  const hits = useHits({ escapeHTML: true })
  return hits.hits.length > 0 ? (
    <div>
      <SectionHeader title="Nyheter" />

      <HitsContainer>
        {hits.hits.map(hit => (
          <Link to={"/" + hit.slug}>
            <div className="uppercase text-xs  text-gray-700">
              {new Date(hit.date).toLocaleDateString("sv-SE")}
            </div>
            <HitHeader>{hit.title}</HitHeader>
            <HitExcerpt>
              <Highlight
                highlightedTagName="mark"
                attribute="excerpt"
                hit={hit}
              />
            </HitExcerpt>
          </Link>
        ))}
      </HitsContainer>
    </div>
  ) : (
    <></>
  )
}

const PolitikHits = () => {
  const { hits } = useHits()
  const hitsByArea = hits.slice(0, 10).reduce((areas, hit) => {
    return {
      ...areas,
      [hit.title]: areas[hit.title] ? [...areas[hit.title], hit] : [hit],
    }
  }, {})
  const hitsArr = Object.entries(hitsByArea)
    .sort(([a1], [a2]) => a1.localeCompare(a2))
    .map(([area, hits]) => (
      <>
        <div className="break-inside-avoid">
          <Link to={hits[0].uri}>
            <HitHeader>{area}</HitHeader>
          </Link>

          <ul className=" break-words list-disc list-outside ml-4 stroke-gray-700">
            {hits.slice(0, 3).map(hit => (
              <PolitikHit hit={hit} />
            ))}
          </ul>
        </div>
      </>
    ))
  return hitsArr.length == 0 ? (
    <></>
  ) : (
    <>
      <SectionHeader title="Politik" />
      <HitsContainer cols={2}>{hitsArr}</HitsContainer>
    </>
  )
}

const SidHits = () => {
  const { hits } = useHits()
  return hits.length > 0 ? (
    <div>
      <SectionHeader title="Sidor" />
      <HitsContainer cols={2}>
        {hits.map(hit => (
          <Link to={"/" + hit.slug} key={hit.objectID}>
            <HitHeader>{hit.title}</HitHeader>

            <HitExcerpt>
              <Highlight
                highlightedTagName="mark"
                attribute="excerpt"
                hit={hit}
              />
            </HitExcerpt>
          </Link>
        ))}
      </HitsContainer>
    </div>
  ) : (
    <></>
  )
}

const PolitikHit = ({ hit }) => (
  <li>
    <Link to={hit.uri + "#" + hit.objectID}>
      <HitExcerpt>
        <Highlight attribute="rubrik" hit={hit} />
      </HitExcerpt>
    </Link>
  </li>
)

const SectionHeader = ({ title }) => (
  <div className=" text-xs font-medium text-gray-700 mb-4 mt-6">
    <hr className="bg-gray-100 h-px border-0 mb-2" />
    {title}
  </div>
)

const HitHeader = (props: PropsWithChildren<{}>) => (
  <div className="font-medium text-sm">{props.children}</div>
)

const HitExcerpt = ({ children }: PropsWithChildren<{}>) => (
  <span className="text-sm">{children}</span>
)

const HitsContainer = ({
  cols = 1,
  children,
}: PropsWithChildren<{ cols: number }>) => (
  <div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-4`}>
    {children}
  </div>
)
